import { useLayoutEffect } from "react";
import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { ThemeProvider, useTheme } from "next-themes";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AccountPage from "@/pages/AccountPage";
import AdminPage from "@/pages/AdminPage";
import CallDetailPage from "@/pages/CallDetailPage";
import CustomerTimelinePage from "@/pages/CustomerTimelinePage";
import CustomersPage from "@/pages/CustomersPage";
import DashboardPage from "@/pages/DashboardPage";
import DataHandlingPage from "@/pages/DataHandlingPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import LegalConsentPage from "@/pages/LegalConsentPage";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import PrivacyPage from "@/pages/PrivacyPage";
import ProfilePage from "@/pages/ProfilePage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import TermsPage from "@/pages/TermsPage";
import UploadPage from "@/pages/UploadPage";
import TranscriptImportPage from "@/pages/TranscriptImportPage";
import PrepareNextCallPage from "@/pages/PrepareNextCallPage";
import IntegrationsPage from "@/pages/IntegrationsPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import LiveDemoCallPage from "@/pages/LiveDemoCallPage";
import LiveDemoRelationshipPage from "@/pages/LiveDemoRelationshipPage";
import LiveDemoUseCasePage from "@/pages/LiveDemoUseCasePage";
import SalesCallReviewPage from "@/pages/SalesCallReviewPage";
import SalesCallAnalysisSoftwarePage from "@/pages/SalesCallAnalysisSoftwarePage";
import ConversationIntelligenceVsCallSummarizationPage from "@/pages/ConversationIntelligenceVsCallSummarizationPage";
import PrepareForNextCustomerCallPage from "@/pages/PrepareForNextCustomerCallPage";
import CrmNotesAfterSalesCallPage from "@/pages/CrmNotesAfterSalesCallPage";
import SalesCallDebriefTemplatePage from "@/pages/SalesCallDebriefTemplatePage";
import FollowUpEmailFromSalesCallTranscriptPage from "@/pages/FollowUpEmailFromSalesCallTranscriptPage";
import SelfCoachingSalesCallsPage from "@/pages/SelfCoachingSalesCallsPage";
import RenewalCallPreparationPage from "@/pages/RenewalCallPreparationPage";
import AboutPage from "@/pages/AboutPage";
import ProductRelationshipMemoryPage from "@/pages/ProductRelationshipMemoryPage";
import ProductCrmNotesPage from "@/pages/ProductCrmNotesPage";
import ProductCoachingPage from "@/pages/ProductCoachingPage";
import ProductPrepareNextCallPage from "@/pages/ProductPrepareNextCallPage";
import { Toaster } from "sonner";
import BrandLockup from "@/components/BrandLockup";
import BetaQuickStartDialog from "@/components/BetaQuickStartDialog";
import AppErrorBoundary from "@/components/AppErrorBoundary";
import AnalyticsRouteTracker from "@/components/AnalyticsRouteTracker";
import SeoManager from "@/components/SeoManager";
import { getRouteBoundaryKey } from "@/lib/routeBoundaryKey";
import { prewarmGoogleIdentityServices } from "@/components/GoogleIdentityButton";

function RouteLoadingScreen() {
  return (
    <div className="app-loading-screen" role="status" aria-label="Loading Callsect">
      <div className="app-loading-screen__content">
        <span className="app-loading-screen__brand">
          <BrandLockup className="brand-lockup--loading" />
        </span>
        <div className="app-loading-screen__lines" aria-hidden="true">
          <span className="app-loading-screen__line" />
          <span className="app-loading-screen__line" />
          <span className="app-loading-screen__line" />
        </div>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading, legalVersion } = useAuth();

  if (loading) return <RouteLoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.legal_version !== legalVersion) return <Navigate to="/legal-consent" replace />;

  const showInitialGuide =
    user.account_type === "beta" &&
    user.onboarding_version_seen !== "beta-launch-v1";

  return (
    <>
      {children}
      <BetaQuickStartDialog
        open={showInitialGuide}
        onClose={() => {}}
        persistOnClose
      />
    </>
  );
}

function AuthenticatedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) return <RouteLoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;

  return children;
}

function NewCallRoute() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  return params.get("mode") === "transcript" ? <TranscriptImportPage /> : <UploadPage />;
}

function LegacyTranscriptImportRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set("mode", "transcript");
  return (
    <Navigate
      to={{ pathname: "/upload", search: `?${params.toString()}` }}
      replace
      state={location.state}
    />
  );
}

function OwnerRoute({ children }) {
  const { user, loading, legalVersion } = useAuth();

  if (loading) return <RouteLoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.legal_version !== legalVersion) return <Navigate to="/legal-consent" replace />;
  if (user.account_type !== "owner") return <Navigate to="/dashboard" replace />;

  return children;
}

function ThemedToaster() {
  const { resolvedTheme } = useTheme();
  return (
    <Toaster
      theme={resolvedTheme === "light" ? "light" : "dark"}
      position="top-right"
      toastOptions={{ className: "callsect-toast" }}
    />
  );
}

function RoutedErrorBoundary({ children }) {
  const location = useLocation();
  return (
    <AppErrorBoundary key={getRouteBoundaryKey(location)}>
      {children}
    </AppErrorBoundary>
  );
}

const WORKSPACE_SCROLL_PREFIXES = [
  "/dashboard",
  "/upload",
  "/import-transcript",
  "/integrations",
  "/calls",
  "/customers",
  "/account",
  "/profile",
  "/admin",
];

function ScrollToTop() {
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    if (typeof window === "undefined") return undefined;

    const isWorkspaceRoute = WORKSPACE_SCROLL_PREFIXES.some(
      (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
    );
    if (isWorkspaceRoute) return undefined;

    const scrollToTop = () => window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    scrollToTop();
    const frame = window.requestAnimationFrame(scrollToTop);

    return () => window.cancelAnimationFrame(frame);
  }, [pathname]);

  return null;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<LoginPage />} />
      <Route path="/register" element={<Navigate to="/signup" replace />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/data-handling" element={<DataHandlingPage />} />
      <Route
        path="/legal-consent"
        element={
          <AuthenticatedRoute>
            <LegalConsentPage />
          </AuthenticatedRoute>
        }
      />
      <Route path="/" element={<LandingPage />} />
      <Route path="/sales-call-review" element={<SalesCallReviewPage />} />
      <Route path="/sales-call-analysis-software" element={<SalesCallAnalysisSoftwarePage />} />
      <Route path="/conversation-intelligence-vs-call-summarization" element={<ConversationIntelligenceVsCallSummarizationPage />} />
      <Route path="/prepare-for-next-customer-call" element={<PrepareForNextCustomerCallPage />} />
      <Route path="/crm-notes-after-sales-call" element={<CrmNotesAfterSalesCallPage />} />
      <Route path="/sales-call-debrief-template" element={<SalesCallDebriefTemplatePage />} />
      <Route path="/follow-up-email-from-sales-call-transcript" element={<FollowUpEmailFromSalesCallTranscriptPage />} />
      <Route path="/self-coaching-sales-calls" element={<SelfCoachingSalesCallsPage />} />
      <Route path="/renewal-call-preparation" element={<RenewalCallPreparationPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/product/relationship-memory" element={<ProductRelationshipMemoryPage />} />
      <Route path="/product/crm-notes" element={<ProductCrmNotesPage />} />
      <Route path="/product/coaching" element={<ProductCoachingPage />} />
      <Route path="/product/prepare-next-call" element={<ProductPrepareNextCallPage />} />
      <Route path="/demo" element={<LiveDemoUseCasePage />} />
      <Route path="/demo/sales" element={<LiveDemoCallPage demoUseCase="sales" />} />
      <Route path="/demo/renewals" element={<LiveDemoCallPage demoUseCase="renewals" />} />
      <Route path="/demo/sales/relationship" element={<LiveDemoRelationshipPage demoUseCase="sales" />} />
      <Route path="/demo/renewals/relationship" element={<LiveDemoRelationshipPage demoUseCase="renewals" />} />
      <Route path="/demo/relationship" element={<Navigate to="/demo/renewals/relationship" replace />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/upload"
        element={
          <ProtectedRoute>
            <NewCallRoute />
          </ProtectedRoute>
        }
      />
      <Route
        path="/import-transcript"
        element={
          <ProtectedRoute>
            <LegacyTranscriptImportRedirect />
          </ProtectedRoute>
        }
      />
      <Route
        path="/integrations"
        element={
          <ProtectedRoute>
            <IntegrationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/calls/:id"
        element={
          <ProtectedRoute>
            <CallDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/account"
        element={
          <ProtectedRoute>
            <AccountPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <OwnerRoute>
            <AdminPage />
          </OwnerRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/prepare-next-call"
        element={
          <ProtectedRoute>
            <PrepareNextCallPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/customers"
        element={
          <ProtectedRoute>
            <CustomersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/customers/:key"
        element={
          <ProtectedRoute>
            <CustomerTimelinePage />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  useLayoutEffect(() => {
    prewarmGoogleIdentityServices().catch(() => {
      // The auth surface already owns the user-visible GIS error state.
    });
  }, []);

  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      storageKey="callsect-public-theme"
      disableTransitionOnChange
    >
      <div className="App min-h-screen font-outfit text-foreground">
        <BrowserRouter>
          <AuthProvider>
            <AnalyticsRouteTracker />
            <ScrollToTop />
            <SeoManager />
            <ThemedToaster />
            <RoutedErrorBoundary>
              <AppRoutes />
            </RoutedErrorBoundary>
          </AuthProvider>
        </BrowserRouter>
      </div>
    </ThemeProvider>
  );
}

export default App;
